import config from "../settings/config";
import { trackFunnel } from "./funnelTracker";

/**
 * Email sign-in with the VERIFICA EMAIL / ACCEDI button (client, 14/09/2026).
 *
 * The app declares what it can handle, and a backend without these features
 * ignores the list and keeps sending a code-only email:
 * - email_link: the email carries a button as well as the code;
 * - account_on_verify: a new address gets its account as soon as it's verified;
 * - terms_notice: the sign-in screen says "Continuando accetti i Termini e
 *   Condizioni…", so the backend records the required agreements.
 */
export const EMAIL_SIGN_IN_CAPABILITIES = ["email_link", "account_on_verify", "terms_notice"];

/** Google and Apple have no email step: only the Terms line applies. */
export const SOCIAL_SIGN_IN_CAPABILITIES = ["terms_notice"];

const ATTEMPT_KEY = "ds_email_signin_attempt";

const apiUrl = (path) => `${config.env[config.env.mode].url}${path}`;

const postJson = async (path, body) => {
  const res = await fetch(apiUrl(path), {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
};

export const sendSignInEmail = (email) =>
  postJson("/api/v1/otp/email/send", { email, capabilities: EMAIL_SIGN_IN_CAPABILITIES });

export const verifySignInCode = (email, code) =>
  postJson("/api/v1/otp/email/verify", { email, code, capabilities: EMAIL_SIGN_IN_CAPABILITIES });

export const verifySignInLink = (verifica, attemptSecret) =>
  postJson("/api/v1/otp/email/verify-link", { verifica, attempt_secret: attemptSecret });

/**
 * The button in the email signs in only the app that asked for it, and this
 * secret is how the app proves it. Kept across restarts, since the member leaves
 * the app to open their mail, until it expires or is used.
 */
export const saveEmailAttempt = ({ email, secret, expiresAt, mode }) => {
  if (!secret) return;
  try {
    localStorage.setItem(
      ATTEMPT_KEY,
      JSON.stringify({ email, secret, expiresAt: expiresAt || null, mode: mode || null }),
    );
  } catch {
    // Storage unavailable: the code in the email still works.
  }
};

export const clearEmailAttempt = () => {
  try {
    localStorage.removeItem(ATTEMPT_KEY);
  } catch {
    // Storage unavailable.
  }
};

export const readEmailAttempt = (now = Date.now()) => {
  let raw = null;
  try {
    raw = localStorage.getItem(ATTEMPT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let attempt = null;
  try {
    attempt = JSON.parse(raw);
  } catch {
    clearEmailAttempt();
    return null;
  }

  const expires = attempt?.expiresAt ? Date.parse(attempt.expiresAt) : NaN;
  if (!attempt?.secret || (Number.isFinite(expires) && expires <= now)) {
    clearEmailAttempt();
    return null;
  }
  return attempt;
};

/** After the code or the button signed the member in: a new account or an existing one. */
export const trackEmailAccountOutcome = (data) => {
  if (data?.is_new_account === true) {
    trackFunnel("registration_completed", { method: "email" });
  } else {
    trackFunnel("login_existing", { method: "email" });
  }
};
